import {
  createStyles,
  withStyles,
  Alert,
  ConfirmationDialog,
  Editor,
  FormSection,
  FormSectionTitle,
  Grid,
  HtmlContent,
  TileList,
  useUser,
  withClientOptions,
  withDialogStackContext
} from '@paintscout/ui'
import type {
  MuiTheme as Theme,
  WithStyles,
  EditorRef,
  WithClientOptions,
  WithDialogStackContext
} from '@paintscout/ui'
import type { MutableRefObject } from 'react'
import React, { useMemo, useState } from 'react'

import type { UpdateableTerms } from '@paintscout/util/builder'
import { getObjectLabels, getTerms, getTermsOptions } from '@paintscout/util/builder'
import { useFlags } from 'shared/components/common/FlagsProvider'

import type { WithQuoteContext } from '../../context/withQuoteContext'
import { withQuoteContext } from '../../context/withQuoteContext'

const styles = (theme: Theme) =>
  createStyles({
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    htmlContent: {
      padding: theme.spacing(),
      border: '2px solid #000'
    }
  })

export interface EditTermsFormProps
  extends WithStyles<typeof styles>,
    WithQuoteContext,
    WithClientOptions,
    WithDialogStackContext {
  editable: boolean
  contentRef: EditorRef
  termsRef?: MutableRefObject<UpdateableTerms>
}

function EditTermsForm(props: EditTermsFormProps) {
  const { quoteContext, clientOptions, dialogStackContext, editable, classes, contentRef, termsRef } = props

  const { options } = clientOptions
  const { quote } = quoteContext
  const user = useUser()
  const { canDeactivateSettingsItems } = useFlags()

  const [terms, setTerms] = useState<UpdateableTerms>(getTerms({ quote, options }))
  termsRef.current = terms
  const termsOptions = useMemo(
    () => getTermsOptions({ options, compileForQuote: quote, canDeactivate: !!canDeactivateSettingsItems }),
    [canDeactivateSettingsItems]
  )
  const [selectedTermsKey, setSelectedTermsKey] = useState(terms?.name ?? null)
  const [termsContent, setTermsContent] = useState(terms.content)
  const [isCustomized, setIsCustomized] = useState(!!terms.custom)

  const customSelected = !selectedTermsKey || terms.custom

  const termsOptionExists = options?.options?.terms?.values?.[terms.name]
  const isTemplate = quote.type === 'quoteTemplate'
  const objectLabels = getObjectLabels({ options })
  const acceptedLock = quote.status.value === 'accepted'

  const termsDisabled = terms.disabled || (options.options?.corporate?.disableTermsEditing && !user.isCorporate)

  console.log({ termsDisabled })

  return (
    <div className={classes.root}>
      <FormSection hideDivider={true} style={{ paddingTop: 0, paddingBottom: 0 }}>
        <Grid container spacing={1}>
          {acceptedLock && (
            <Grid item xs={12}>
              <Alert severity={'warning'}>Terms of Accepted {objectLabels.quote.plural} cannot be changed.</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormSectionTitle
              title={'Select Existing Terms'}
              variant="h6"
              subTitle={'Load an existing set of terms from the Options.'}
            />
          </Grid>
          <Grid item xs={12}>
            <TileList
              items={termsOptions.map((t) => ({
                ...t,
                key: t.name,
                disabled: acceptedLock
              }))}
              selected={selectedTermsKey}
              dense={selectedTermsKey !== null || customSelected}
              variant="select"
              customTile={{
                selected: customSelected
              }}
              onSelect={(ev, key) => handleTileChange(ev, key as string)}
              onCustom={(ev) => handleTileChange(ev, null, true)}
              disabled={!editable || acceptedLock}
            />
          </Grid>
        </Grid>
      </FormSection>
      {terms.disabled && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity={'warning'}>
              These terms have been customized to include images, videos, or other content not editable here. Please
              contact us for assistance editing these terms.
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <HtmlContent classes={{ root: classes.htmlContent }} content={terms.content} />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          {isTemplate && (
            <>
              {terms.custom && (
                <Alert severity={'warning'}>
                  These terms have been customized from the selected terms. New{' '}
                  {objectLabels.quote.plural.toLowerCase()} will use these customized terms.
                </Alert>
              )}
              {!terms.custom && (
                <>
                  {!!terms?.name && termsOptionExists && (
                    <>
                      {quote.smartTemplate ? (
                        <Alert severity={'info'}>
                          These terms are set by the {objectLabels.quote.plural.toLowerCase()} type. The most current
                          version of these terms will be used on new {objectLabels.quote.plural.toLowerCase()}{' '}
                        </Alert>
                      ) : (
                        <Alert severity={'info'}>
                          The most current version of these terms will be used on new{' '}
                          {objectLabels.quote.plural.toLowerCase()}{' '}
                        </Alert>
                      )}
                    </>
                  )}
                  {!!terms?.name && !termsOptionExists && (
                    <Alert severity={'error'}>
                      The previously selected terms could not be located in your Account Settings. The existing terms
                      below will be used on new {objectLabels.quote.plural.toLowerCase()}.
                    </Alert>
                  )}
                </>
              )}
            </>
          )}
        </Grid>
        <Grid item container style={{ paddingRight: 4 }} spacing={1} xs={12}>
          <Grid item xs={12}>
            <FormSectionTitle
              title={'Customize Terms'}
              variant="h6"
              subTitle={`Make changes to the terms for this ${objectLabels.quote.value} only.`}
            />
          </Grid>
          <Grid style={{ paddingRight: 0 }} item xs={12}>
            <Editor
              disabled={termsDisabled || !editable || acceptedLock}
              key={terms.name}
              content={termsContent}
              fullWidth={true}
              ref={contentRef}
              toolbar={{
                headings: true,
                align: true
              }}
              onChange={(_val) => {
                if (!isCustomized) {
                  setIsCustomized(true)
                }
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </div>
  )

  async function handleTileChange(event: any, key: string, _custom?: boolean) {
    if (key === null) {
      return setSelectedTermsKey(null)
    }

    const updatedTerms = {
      ...termsOptions.find((t) => t.name === key),
      custom: false
    }

    if (!isCustomized) {
      return updateTerms()
    }

    dialogStackContext.openDialog(ConfirmationDialog, {
      message: 'Any customizations to existing Terms will be lost.',
      onConfirm: () => {
        updateTerms()
        dialogStackContext.dismissDialog()
      },
      onCancel: () => {
        dialogStackContext.dismissDialog()
      }
    })

    function updateTerms() {
      setTerms(updatedTerms)
      termsRef.current = updatedTerms
      setSelectedTermsKey(key)
      setTermsContent(updatedTerms.content)
      setIsCustomized(false)
    }
  }
}

export default withStyles(styles)(withClientOptions(withQuoteContext(withDialogStackContext(EditTermsForm))))
